import { actions } from 'Data/ReduxViewModels/Images';
import { ImageSize } from 'Data/ReduxViewModels/Images/types';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { CalculateFileSizeFromBase64, ExtractBase64FromDataUrl } from 'Lib/File';
import { formatFileSize } from 'Lib/Format';
import { useHasChangedAfterFirstLoad, usePrevious } from 'Lib/Hooks';
import { pipe } from 'Lib/Hooks/Selectors';
import { IImageResizeSettings } from 'Lib/Image';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Field, InlineSpinner, TextOutput } from '..';
import { withInjectedProps } from '../InjectedPropsContext';
import { ChangeEventWithData } from '../types';
import FileUpload, { IFileUploadProps } from './FileUpload';
import './ImageUpload.scss';

export type IImageUploadProps = Omit<IFileUploadProps, 'onChange'> & {
  onChange: (e: ChangeEventWithData<HTMLInputElement, ImageValue | null>) => void;
  resizeSettings?: IImageResizeSettings;
};

/**
 * Represents the output "value" of an image upload component
 */
export type ImageValue = {
  /**
   * The selected image (post-processing) as a data url
   */
  dataUrl: string;
  /**
   * The original filename of the image
   */
  filename: string;
};

let idCounter = 0;

const getSummaryFromImageSize = (s: ImageSize) => `${s?.width}×${s?.height}px (${formatFileSize(s?.fileSizeInBytes!)})`;

const ImageUploadBase: React.FC<IImageUploadProps> = ({ id, placeholder, label, onChange, resizeSettings }) => {
  const dispatch = useDispatch();

  const [myId] = useState(id ?? `imageUpload_${idCounter++}`);
  const [deferredChangeEvent, setDeferredChangeEvent] = useState<ChangeEventWithData<HTMLInputElement, File>>();

  useEffect(() => {
    return () => {
      dispatch(actions.cleanupImageProcessing({ id: myId }));
    };
  }, [dispatch, myId]);

  const status = useSelector((s) => s.images.processing[myId]?.status);
  const processedImage = useSelector(
    (s) =>
      s.images.processing[myId]?.processedDataUrl
        ? ({
            dataUrl: s.images.processing[myId].processedDataUrl,
            filename: s.images.processing[myId].filename,
          } as ImageValue)
        : null,
    shallowEqual
  );

  const imageWasResized = useSelector((s) => hasNonEmptyValue(s.images.processing[myId]?.resizing));
  const originalImageSummary = useSelector(
    pipe(
      (state) => state.images.processing[myId]?.resizing?.originalSize,
      (s) => (hasNonEmptyValue(s) ? `Original image: ${getSummaryFromImageSize(s!)}` : undefined)
    )
  );
  const resizedImageSummary = useSelector(
    pipe(
      (state) => state.images.processing[myId]?.resizing?.resizedSize,
      (s) => (hasNonEmptyValue(s) ? `Resized image: ${getSummaryFromImageSize(s!)}` : undefined)
    )
  );

  const [currentObjectUri, setCurrentObjectUrl] = useState<string>();
  const previousObjectUri = usePrevious(currentObjectUri);

  // When selected file changes, kick off processing of selected image.
  const onFileChange = (e: ChangeEventWithData<HTMLInputElement, File>) => {
    var file = e.data?.value;
    if (hasValue(file)) {
      // We create an object url to have a serialisable reference to the selected file
      // (the alternative is to override these actions to allow unserialisable data)
      var objectUrl = URL.createObjectURL(file!);
      setCurrentObjectUrl(objectUrl);
      setDeferredChangeEvent(e);
      dispatch(
        actions.processImage({
          id: myId,
          fileUrl: URL.createObjectURL(file!),
          filename: file!.name,
          fileSizeInBytes: file!.size,
          filetype: file!.type,
          resizeSettings,
        })
      );
    } else {
      setCurrentObjectUrl(undefined);
      setDeferredChangeEvent(undefined);
      dispatch(actions.cleanupImageProcessing({ id: myId }));
    }
  };

  // When image finishes processing, call onChange (using a deferred change event if available)
  const hasStatusChanged = useHasChangedAfterFirstLoad(status);
  useEffect(() => {
    if (hasStatusChanged && status !== 'processing') {
      var originalEvent = deferredChangeEvent ?? ({} as ChangeEventWithData<HTMLInputElement, File>);
      var event = originalEvent as ChangeEventWithData<HTMLInputElement, ImageValue | null>;
      event.data = {
        value: processedImage,
      };
      onChange(event);
      setDeferredChangeEvent(undefined);
    }
  }, [processedImage, status, hasStatusChanged, deferredChangeEvent, onChange]);

  // Clean up unused object URLs
  useEffect(() => {
    if (previousObjectUri !== currentObjectUri && hasValue(previousObjectUri)) {
      URL.revokeObjectURL(previousObjectUri!);
    }
    return () => {
      if (hasValue(currentObjectUri)) URL.revokeObjectURL(currentObjectUri!);
    };
  }, [previousObjectUri, currentObjectUri]);

  return (
    <>
      <FileUpload
        onChange={onFileChange}
        label={label ?? 'Select Image'}
        placeholder={placeholder ?? 'Select an Image to upload'}
        // There are a number of image file types our backend will not support
        // However, some devices use "image/*" as a hint to prompt for camera input (see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#limiting_accepted_file_types)
        // So we include these types, and raise an appropriate error
        acceptFileExtensions="image/*"
      />
      <div className={classNames('Image-Upload-Summary')}>
        {status === 'processing' ? (
          <>
            Processing image...
            <InlineSpinner />
          </>
        ) : imageWasResized ? (
          <>
            <Field input={<TextOutput />} value={originalImageSummary} blankReplacement="" />
            <Field input={<TextOutput />} value={resizedImageSummary} blankReplacement="" />
          </>
        ) : hasValue(processedImage?.dataUrl) ? (
          // Where image wasn't resized, we can only show file size of the data that will be uploaded.
          <>
            <Field
              input={<TextOutput />}
              value={`File size: ${formatFileSize(
                CalculateFileSizeFromBase64(ExtractBase64FromDataUrl(processedImage?.dataUrl!))
              )}`}
              blankReplacement=""
            />
          </>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

const ImageUpload = withInjectedProps(ImageUploadBase);

export { ImageUpload };
export default ImageUpload;
